import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig, type AxiosResponse } from "axios";

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
// Fallback to localhost:5500 for development if env variable is not set
// Production: https://apiv1.impactadvisoryafrica.com/api/v1
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  message?: string;
  data?: {
    accessToken: string;
    refreshToken: string;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
  details?: {
    field: string;
    suggestion: string;
  };
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  success: boolean;
  message?: string;
  data?: {
    accessToken: string;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface MyPermissionsResponse {
  success: boolean;
  data?: {
    permissions: string[];
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface EventUser {
  id: string;
  fname: string;
  lname: string;
  avatar: string | null;
}

export interface EventParticipant {
  id: string;
  userId: string;
  eventId: string;
  user: EventUser;
  createdAt: string;
  updatedAt: string;
}

export interface IBBRegistration {
  id: string;
  name: string;
  email: string;
  personalPhoneNumber: string;
  officePhoneNumber: string;
  personalEmail: string;
  officeEmail: string | null;
  companyName: string;
  role: string;
  roleOther: string | null;
  registrationType: string;
  registrationTypeOther: string | null;
  attendance: string;
  sponsorPartner: string;
  sponsorPartnerOther: string | null;
  expectations: string;
  paymentTerms: string;
  statusPaid: string;
  markedAttended: boolean;
  eventId: string;
  registeredAt: string;
  updatedAt: string;
  /** Last invitation email attempt */
  lastInvitationSentAt?: string | null;
  lastInvitationStatus?: "success" | "failed" | null;
  lastInvitationError?: string | null;
  /** When this participant was contacted by phone (outreach / call session); null = not logged */
  phoneOutreachAt?: string | null;
}

export interface IBBRegistrationsQueryParams {
  page?: number;
  limit?: number;
  event_id?: string;
  status_paid?: string;
  search?: string;
  registration_type?: string;
  role?: string;
  company_name?: string;
  sponsor_partner?: string;
  payment_terms?: string;
  attendance?: string;
  marked_attended?: string;
  sort_by?: string;
  sort_order?: string;
  start_date?: string;
  end_date?: string;
}

export interface IBBRegistrationsResponse {
  success: boolean;
  data?: {
    registrations: IBBRegistration[];
    pagination: EventsPagination;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface CreateIBBRegistrationRequest {
  email: string;
  name: string;
  personalPhoneNumber: string;
  officePhoneNumber: string;
  personalEmail: string;
  companyName: string;
  role: string;
  registrationType: string;
  sponsorPartner: string;
  expectations: string;
  paymentTerms: string;
  officeEmail?: string;
  roleOther?: string;
  registrationTypeOther?: string;
  attendance?: string;
  sponsorPartnerOther?: string;
}

export interface UpdateIBBRegistrationRequest {
  email?: string;
  name?: string;
  personalPhoneNumber?: string;
  officePhoneNumber?: string;
  personalEmail?: string;
  companyName?: string;
  role?: string;
  roleOther?: string;
  registrationType?: string;
  sponsorPartner?: string;
  expectations?: string;
  paymentTerms?: string;
  eventId?: string;
  markedAttended?: boolean;
}

export interface UpdateIBBRegistrationStatusRequest {
  statusPaid: string;
}

export interface Event {
  id: string;
  title: string;
  type: string;
  edition: string;
  category: string;
  description: string;
  eventPlace: string;
  eventDate: string;
  userId: string;
  user: EventUser;
  createdAt: string;
  updatedAt: string;
  media?: unknown[];
  speakers?: unknown[];
}

export interface EventsPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface EventsResponse {
  success: boolean;
  data?: {
    events: Event[];
    pagination: EventsPagination;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface EventsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  upcoming?: boolean;
  user_id?: string;
}

export interface CreateEventRequest {
  title: string;
  type: string;
  edition?: string;
  category: string;
  description: string;
  eventPlace?: string;
  eventDate: string;
}

export interface UpdateEventRequest {
  title?: string;
  type?: string;
  edition?: string;
  category?: string;
  description?: string;
  eventPlace?: string;
  eventDate?: string;
}

export interface EventResponse {
  success: boolean;
  data?: Event;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface EventDetails extends Event {
  participants?: EventParticipant[];
}

export interface EventDetailsResponse {
  success: boolean;
  data?: {
    event: EventDetails;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface ApiError {
  success: false;
  error: string;
  code: string;
  statusCode: number;
  timestamp: string;
  details?: {
    field?: string;
    suggestion?: string;
    fieldErrors?: {
      [fieldName: string]: {
        message: string;
        code: string;
      };
    };
    totalErrors?: number;
  };
}

// Dashboard API interfaces
export interface DashboardStats {
  totalEvents: {
    current: number;
    previous: number;
    change: number;
    changeType: "positive" | "negative";
  };
  totalRegistrations: {
    current: number;
    previous: number;
    change: number;
    changeType: "positive" | "negative";
  };
  blogVisitors: {
    current: number;
    previous: number;
    change: number;
    changeType: "positive" | "negative";
  };
  blogPosts: {
    current: number;
    previous: number;
    change: number;
    changeType: "positive" | "negative";
  };
}

export interface DashboardStatsResponse {
  success: boolean;
  data?: DashboardStats;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface ChartDataPoint {
  day?: string;
  month?: string;
  year?: string;
  time?: string;
  events?: number;
  Online?: number;
  InPerson?: number;
}

export interface EventsChartResponse {
  success: boolean;
  data?: {
    chartData: ChartDataPoint[];
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface RegistrationsChartResponse {
  success: boolean;
  data?: {
    chartData: ChartDataPoint[];
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface RecentRegistration {
  id: number | string;
  name: string;
  event: string;
  eventId: string;
  registeredOn: string;
  type: string;
  status: string;
  statusPaid: string;
  markedAttended?: boolean;
}

export interface RecentRegistrationsResponse {
  success: boolean;
  data?: {
    registrations: RecentRegistration[];
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface BlogPost {
  id: string;
  title: string;
  slug?: string;
  content?: string;
  excerpt?: string;
  author?: {
    id: string;
    name: string;
    avatar?: string;
  };
  views: number;
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
  tags?: string[];
  category?: string;
}

export interface BlogPostsResponse {
  success: boolean;
  data?: {
    posts: BlogPost[];
    pagination: EventsPagination;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface TopBlogPost {
  id: string;
  title: string;
  views: number;
  percentage: number;
  emoji: string;
}

export interface TopBlogPostsResponse {
  success: boolean;
  data?: {
    posts: TopBlogPost[];
    totalViews: number;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface BlogTrafficSource {
  name: string;
  value: number;
  color: string;
  visitors: number;
}

export interface BlogTrafficResponse {
  success: boolean;
  data?: {
    trafficSources: BlogTrafficSource[];
    totalVisitors: number;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface BlogStats {
  totalVisitors: {
    current: number;
    previous: number;
    change: number;
    changeType: "positive" | "negative";
  };
  totalPosts: {
    current: number;
    previous: number;
    change: number;
    changeType: "positive" | "negative";
  };
  averageViewsPerPost: number;
  totalViews: number;
}

export interface BlogStatsResponse {
  success: boolean;
  data?: BlogStats;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

// Token storage utilities
export const tokenStorage = {
  getAccessToken: (): string | null => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem("accessToken");
  },
  getRefreshToken: (): string | null => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem("refreshToken");
  },
  setTokens: (accessToken: string, refreshToken: string): void => {
    if (typeof window === "undefined") return;
    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("refreshToken", refreshToken);
  },
  clearTokens: (): void => {
    if (typeof window === "undefined") return;
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
  },
  isAuthenticated: (): boolean => {
    return !!tokenStorage.getAccessToken();
  },
};

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const accessToken = tokenStorage.getAccessToken();
    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    // Remove Content-Type header for FormData so axios can set it automatically with boundary
    if (config.data instanceof FormData && config.headers) {
      delete config.headers["Content-Type"];
    }
    return config;
  },
  (error: unknown) => {
    return Promise.reject(error);
  }
);

// Track if we're currently refreshing to avoid multiple refresh calls
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (error?: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Response interceptor for error handling and token refresh
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await refreshToken();

        if (response.success && response.data) {
          const newAccessToken = response.data.accessToken;
          processQueue(null, newAccessToken);

          // Retry original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          }
          return apiClient(originalRequest);
        } else {
          processQueue(error, null);
          return Promise.reject(error);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        // Redirect to login on refresh failure
        if (typeof window !== "undefined") {
          tokenStorage.clearTokens();
          window.location.href = "/login";
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other errors
    if (error.response) {
      // Server responded with error status
      const errorData = error.response.data as ApiError;
      return Promise.reject({
        ...errorData,
        statusCode: error.response.status,
      } as ApiError);
    } else if (error.request) {
      // Request was made but no response received
      return Promise.reject({
        success: false,
        error: "Network error. Please check your connection.",
        code: "NETWORK_ERROR",
        statusCode: 0,
        timestamp: new Date().toISOString(),
      } as ApiError);
    } else {
      // Something else happened
      return Promise.reject({
        success: false,
        error: error.message || "An unexpected error occurred",
        code: "UNKNOWN_ERROR",
        statusCode: 0,
        timestamp: new Date().toISOString(),
      } as ApiError);
    }
  }
);

// API client function (for backward compatibility)
export async function apiRequest<T>(
  endpoint: string,
  options: {
    method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
    data?: unknown;
    params?: Record<string, unknown>;
  } = {}
): Promise<T> {
  const { method = "GET", data, params } = options;

  const response = await apiClient.request<T>({
    url: endpoint,
    method,
    data,
    params,
  });

  return response.data;
}

// Auth API functions
export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  try {
    const response = await apiClient.post<LoginResponse>("/auth/login", credentials);

    if (response.data.success && response.data.data) {
      tokenStorage.setTokens(
        response.data.data.accessToken,
        response.data.data.refreshToken
      );
    }

    return response.data;
  } catch (error) {
    // Error is already handled by interceptor, just rethrow
    throw error;
  }
}

// Separate axios instance for refresh token calls (to avoid interceptor loop)
const refreshClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

export async function refreshToken(): Promise<RefreshTokenResponse> {
  const refreshTokenValue = tokenStorage.getRefreshToken();
  
  if (!refreshTokenValue) {
    throw {
      success: false,
      error: "No refresh token available",
      code: "NO_REFRESH_TOKEN",
      statusCode: 401,
      timestamp: new Date().toISOString(),
    } as ApiError;
  }

  try {
    // Use refreshClient to avoid triggering the interceptor
    const response = await refreshClient.post<RefreshTokenResponse>("/auth/refresh", {
      refreshToken: refreshTokenValue,
    });

    if (response.data.success && response.data.data) {
      // Update access token, keep the same refresh token
      const currentRefreshToken = tokenStorage.getRefreshToken();
      if (currentRefreshToken) {
        tokenStorage.setTokens(
          response.data.data.accessToken,
          currentRefreshToken
        );
      }
    }

    return response.data;
  } catch (error) {
    // If refresh fails, clear tokens and rethrow
    tokenStorage.clearTokens();
    throw error;
  }
}

export function logout(): void {
  tokenStorage.clearTokens();
}

export async function getMyPermissions(): Promise<MyPermissionsResponse> {
  try {
    const response = await apiClient.get<MyPermissionsResponse>("/permissions/me");
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Events API functions
export async function getEvents(params: EventsQueryParams = {}): Promise<EventsResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page !== undefined) {
      queryParams.append("page", params.page.toString());
    }
    if (params.limit !== undefined) {
      queryParams.append("limit", params.limit.toString());
    }
    if (params.search) {
      queryParams.append("search", params.search);
    }
    if (params.category) {
      queryParams.append("category", params.category);
    }
    if (params.upcoming !== undefined) {
      queryParams.append("upcoming", params.upcoming.toString());
    }
    if (params.user_id) {
      queryParams.append("user_id", params.user_id);
    }

    const queryString = queryParams.toString();
    const endpoint = `/events${queryString ? `?${queryString}` : ""}`;
    
    const response = await apiClient.get<EventsResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function createEvent(data: CreateEventRequest): Promise<EventResponse> {
  try {
    const response = await apiClient.post<EventResponse>("/events", data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updateEvent(eventId: string, data: UpdateEventRequest): Promise<EventResponse> {
  try {
    const response = await apiClient.put<EventResponse>(`/events/${eventId}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function deleteEvent(eventId: string): Promise<EventResponse> {
  try {
    const response = await apiClient.delete<EventResponse>(`/events/${eventId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getEventDetails(eventId: string): Promise<EventDetailsResponse> {
  try {
    const response = await apiClient.get<EventDetailsResponse>(`/events/${eventId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getIBBRegistrations(eventId: string, params: Omit<IBBRegistrationsQueryParams, 'event_id'> = {}): Promise<IBBRegistrationsResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page !== undefined) {
      queryParams.append("page", params.page.toString());
    }
    if (params.limit !== undefined) {
      queryParams.append("limit", params.limit.toString());
    }
    if (params.status_paid) {
      queryParams.append("status_paid", params.status_paid);
    }
    if (params.search) {
      queryParams.append("search", params.search);
    }
    if (params.registration_type) {
      queryParams.append("registration_type", params.registration_type);
    }
    if (params.role) {
      queryParams.append("role", params.role);
    }
    if (params.company_name) {
      queryParams.append("company_name", params.company_name);
    }
    if (params.sponsor_partner) {
      queryParams.append("sponsor_partner", params.sponsor_partner);
    }
    if (params.payment_terms) {
      queryParams.append("payment_terms", params.payment_terms);
    }
    if (params.attendance) {
      queryParams.append("attendance", params.attendance);
    }
    if (params.marked_attended) {
      queryParams.append("marked_attended", params.marked_attended);
    }
    if (params.sort_by) {
      queryParams.append("sort_by", params.sort_by);
    }
    if (params.sort_order) {
      queryParams.append("sort_order", params.sort_order);
    }
    if (params.start_date) {
      queryParams.append("start_date", params.start_date);
    }
    if (params.end_date) {
      queryParams.append("end_date", params.end_date);
    }

    const queryString = queryParams.toString();
    const endpoint = `/registrations/ibb/${eventId}${queryString ? `?${queryString}` : ""}`;
    
    const response = await apiClient.get<IBBRegistrationsResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function createIBBRegistration(eventId: string, data: Omit<CreateIBBRegistrationRequest, 'eventId'>): Promise<EventResponse> {
  try {
    const response = await apiClient.post<EventResponse>(`/registrations/ibb/${eventId}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updateIBBRegistration(eventId: string, registrationId: string, data: UpdateIBBRegistrationRequest): Promise<EventResponse> {
  try {
    const response = await apiClient.put<EventResponse>(`/registrations/ibb/${eventId}/${registrationId}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updateIBBRegistrationStatus(eventId: string, registrationId: string, data: UpdateIBBRegistrationStatusRequest): Promise<EventResponse> {
  try {
    const response = await apiClient.put<EventResponse>(`/registrations/ibb/${eventId}/${registrationId}/status`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function deleteIBBRegistration(eventId: string, registrationId: string): Promise<EventResponse> {
  try {
    const response = await apiClient.delete<EventResponse>(`/registrations/ibb/${eventId}/${registrationId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export interface UpdateAttendanceRequest {
  markedAttended: boolean;
}

export interface UpdateAttendanceResponse {
  success: boolean;
  message?: string;
  data?: {
    registration: {
      id: string;
      markedAttended: boolean;
      updatedAt: string;
    };
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export async function updateIBBRegistrationAttendance(
  eventId: string,
  registrationId: string,
  data: UpdateAttendanceRequest
): Promise<UpdateAttendanceResponse> {
  try {
    const response = await apiClient.put<UpdateAttendanceResponse>(
      `/registrations/ibb/${eventId}/${registrationId}/attendance`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

export interface UpdateIBBPhoneOutreachRequest {
  /** ISO datetime string, or null to clear */
  phoneOutreachAt: string | null;
}

export interface UpdateIBBPhoneOutreachResponse {
  success: boolean;
  message?: string;
  data?: {
    registration: IBBRegistration;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export async function updateIBBPhoneOutreach(
  eventId: string,
  registrationId: string,
  data: UpdateIBBPhoneOutreachRequest
): Promise<UpdateIBBPhoneOutreachResponse> {
  try {
    const response = await apiClient.put<UpdateIBBPhoneOutreachResponse>(
      `/registrations/ibb/${eventId}/${registrationId}/outreach`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

/** Append-only IBB communication history (per event) */
export interface IBBCommunicationLogEntry {
  id: string;
  eventId: string;
  registrationId: string;
  kind: "invitation_email" | "phone_outreach";
  status: string;
  errorMessage: string | null;
  registrantName: string | null;
  registrantEmail: string | null;
  occurredAt: string;
  createdAt: string;
}

export interface IBBCommunicationLogQueryParams {
  page?: number;
  limit?: number;
  kind?: "invitation_email" | "phone_outreach";
  /** When set, only log rows for this IBB registration */
  registrationId?: string;
}

export interface IBBCommunicationLogResponse {
  success: boolean;
  data?: {
    entries: IBBCommunicationLogEntry[];
    pagination: EventsPagination;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export async function getIBBCommunicationLog(
  eventId: string,
  params?: IBBCommunicationLogQueryParams
): Promise<IBBCommunicationLogResponse> {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set("page", String(params.page));
  if (params?.limit) searchParams.set("limit", String(params.limit));
  if (params?.kind) searchParams.set("kind", params.kind);
  if (params?.registrationId) searchParams.set("registration_id", params.registrationId);
  const queryString = searchParams.toString();
  const endpoint = `/registrations/ibb/${eventId}/logs/communication${queryString ? `?${queryString}` : ""}`;
  try {
    const response = await apiClient.get<IBBCommunicationLogResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// IBB Student Registration interfaces
export interface IBBStudentRegistration {
  id: string;
  email: string;
  fullName: string;
  mobileNumber?: string;
  nationalIdNumber?: string;
  studentIdNumber?: string;
  university?: string;
  faculty?: string;
  course?: string;
  courseDetailed?: string;
  yearOfStudy?: string;
  studentLeadership?: boolean;
  leadershipPosition?: string;
  whyAttend?: string;
  topicsOfInterest?: string;
  expectations?: string;
  howDidYouHear?: string;
  informationConfirmed?: boolean;
  agreeToContact?: boolean;
  documentUploaded?: string;
  statusPaid: string;
  markedAttended: boolean;
  eventId: string;
  registeredAt: string;
  updatedAt: string;
}

export interface IBBStudentRegistrationsQueryParams {
  page?: number;
  limit?: number;
  event_id?: string;
  status_paid?: string;
  marked_attended?: boolean;
  search?: string;
  university?: string;
  faculty?: string;
  course?: string;
  year_of_study?: string;
  student_leadership?: boolean;
  start_date?: string;
  end_date?: string;
  sort_by?: string;
  sort_order?: string;
}

export interface IBBStudentRegistrationsResponse {
  success: boolean;
  data?: {
    registrations: IBBStudentRegistration[];
    pagination: EventsPagination;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface CreateIBBStudentRegistrationRequest {
  email: string;
  fullName: string;
  mobileNumber?: string;
  nationalIdNumber?: string;
  studentIdNumber?: string;
  university?: string;
  faculty?: string;
  course?: string;
  courseDetailed?: string;
  yearOfStudy?: string;
  studentLeadership?: boolean;
  leadershipPosition?: string;
  whyAttend?: string;
  topicsOfInterest?: string;
  expectations?: string;
  howDidYouHear?: string;
  informationConfirmed?: boolean;
  agreeToContact?: boolean;
  documentUploaded?: string;
  eventId: string;
}

export interface UpdateIBBStudentRegistrationRequest {
  email?: string;
  fullName?: string;
  mobileNumber?: string;
  nationalIdNumber?: string;
  studentIdNumber?: string;
  university?: string;
  faculty?: string;
  course?: string;
  courseDetailed?: string;
  yearOfStudy?: string;
  studentLeadership?: boolean;
  leadershipPosition?: string;
  whyAttend?: string;
  topicsOfInterest?: string;
  expectations?: string;
  howDidYouHear?: string;
  informationConfirmed?: boolean;
  agreeToContact?: boolean;
  documentUploaded?: string;
  statusPaid?: string;
  markedAttended?: boolean;
}

export interface UpdateIBBStudentRegistrationStatusRequest {
  statusPaid: string;
}

export interface IBBStudentRegistrationResponse {
  success: boolean;
  message?: string;
  data?: {
    registration: IBBStudentRegistration;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

// IBB Student Registration API functions
export async function getIBBStudentRegistrations(
  eventId?: string,
  params: Omit<IBBStudentRegistrationsQueryParams, 'event_id'> & { event_id?: string } = {}
): Promise<IBBStudentRegistrationsResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    // When eventId is provided, we use the path parameter endpoint `/registrations/ibb/${eventId}/students`
    // so we don't add event_id to the query string. When eventId is not provided, we use the general
    // endpoint `/registrations/ibb/students` which can optionally filter by event_id in query params.
    if (!eventId && params.event_id) {
      queryParams.append("event_id", params.event_id);
    }
    
    if (params.page !== undefined) {
      queryParams.append("page", params.page.toString());
    }
    if (params.limit !== undefined) {
      queryParams.append("limit", params.limit.toString());
    }
    if (params.status_paid) {
      queryParams.append("status_paid", params.status_paid);
    }
    if (params.marked_attended !== undefined) {
      queryParams.append("marked_attended", params.marked_attended.toString());
    }
    if (params.search) {
      queryParams.append("search", params.search);
    }
    if (params.university) {
      queryParams.append("university", params.university);
    }
    if (params.faculty) {
      queryParams.append("faculty", params.faculty);
    }
    if (params.course) {
      queryParams.append("course", params.course);
    }
    if (params.year_of_study) {
      queryParams.append("year_of_study", params.year_of_study);
    }
    if (params.student_leadership !== undefined) {
      queryParams.append("student_leadership", params.student_leadership.toString());
    }
    if (params.start_date) {
      queryParams.append("start_date", params.start_date);
    }
    if (params.end_date) {
      queryParams.append("end_date", params.end_date);
    }
    if (params.sort_by) {
      queryParams.append("sort_by", params.sort_by);
    }
    if (params.sort_order) {
      queryParams.append("sort_order", params.sort_order);
    }

    const queryString = queryParams.toString();
    const endpoint = eventId 
      ? `/registrations/ibb/${eventId}/students${queryString ? `?${queryString}` : ""}`
      : `/registrations/ibb/students${queryString ? `?${queryString}` : ""}`;
    
    const response = await apiClient.get<IBBStudentRegistrationsResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getIBBStudentRegistrationById(
  eventId: string,
  registrationId: string
): Promise<IBBStudentRegistrationResponse> {
  try {
    const response = await apiClient.get<IBBStudentRegistrationResponse>(
      `/registrations/ibb/${eventId}/students/${registrationId}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Public create endpoint (no authentication required)
export async function createIBBStudentRegistration(
  data: CreateIBBStudentRegistrationRequest
): Promise<IBBStudentRegistrationResponse> {
  try {
    // Use axios directly without auth header for public endpoint
    const response = await axios.post<IBBStudentRegistrationResponse>(
      `${API_BASE_URL}/registrations/ibb/students`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Public update endpoint (no authentication required)
export interface UpdateIBBStudentRegistrationPublicRequest {
  email: string; // Required to identify the registration
  mobileNumber?: string;
  nationalIdNumber?: string;
  studentIdNumber?: string;
  university?: string;
  faculty?: string;
  course?: string;
  courseDetailed?: string;
  yearOfStudy?: string;
  studentLeadership?: boolean;
  leadershipPosition?: string;
  whyAttend?: string;
  topicsOfInterest?: string;
  expectations?: string;
  howDidYouHear?: string;
  informationConfirmed?: boolean;
  agreeToContact?: boolean;
  documentUploaded?: string;
}

export async function updateIBBStudentRegistrationPublic(
  eventId: string,
  data: UpdateIBBStudentRegistrationPublicRequest
): Promise<IBBStudentRegistrationResponse> {
  try {
    // Create a request without auth header for public endpoint
    const response = await axios.put<IBBStudentRegistrationResponse>(
      `${API_BASE_URL}/registrations/ibb/${eventId}/students/update`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Admin update endpoint (requires authentication)
export async function updateIBBStudentRegistration(
  eventId: string,
  registrationId: string,
  data: UpdateIBBStudentRegistrationRequest
): Promise<IBBStudentRegistrationResponse> {
  try {
    const response = await apiClient.put<IBBStudentRegistrationResponse>(
      `/registrations/ibb/${eventId}/students/${registrationId}`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updateIBBStudentRegistrationStatus(
  eventId: string,
  registrationId: string,
  data: UpdateIBBStudentRegistrationStatusRequest
): Promise<IBBStudentRegistrationResponse> {
  try {
    const response = await apiClient.put<IBBStudentRegistrationResponse>(
      `/registrations/ibb/${eventId}/students/${registrationId}/status`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updateIBBStudentRegistrationAttendance(
  eventId: string,
  registrationId: string,
  data: UpdateAttendanceRequest
): Promise<UpdateAttendanceResponse> {
  try {
    const response = await apiClient.put<UpdateAttendanceResponse>(
      `/registrations/ibb/${eventId}/students/${registrationId}/attendance`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function deleteIBBStudentRegistration(
  eventId: string,
  registrationId: string
): Promise<EventResponse> {
  try {
    const response = await apiClient.delete<EventResponse>(
      `/registrations/ibb/${eventId}/students/${registrationId}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function importIBBStudentRegistrations(
  eventId: string,
  file: File
): Promise<ImportResponse> {
  try {
    const formData = new FormData();
    formData.append("file", file);
    
    const response = await apiClient.post<ImportResponse>(
      `/registrations/ibb/${eventId}/students/import`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

// IBA Registration interfaces (for future use)
export interface IBARegistration {
  id: string;
  email: string;
  fullName: string;
  personalNumber: string;
  officeNumber: string;
  personalEmail: string;
  workEmail: string;
  companyName: string;
  role: string;
  registrationType: string;
  sponsorPartner: string;
  expectations: string;
  paymentTerms: string;
  statusPaid: string;
  attendees: number;
  markedAttended: boolean;
  eventId: string;
  createdAt: string;
  updatedAt: string;
}

export interface IBARegistrationsQueryParams {
  page?: number;
  limit?: number;
  event_id?: string;
  status_paid?: string;
  search?: string;
  marked_attended?: string;
  sort_by?: string;
  sort_order?: string;
  start_date?: string;
  end_date?: string;
}

export interface IBARegistrationsResponse {
  success: boolean;
  data?: {
    registrations: IBARegistration[];
    pagination: EventsPagination;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export async function getIBARegistrations(
  eventId: string,
  params: Omit<IBARegistrationsQueryParams, 'event_id'> = {}
): Promise<IBARegistrationsResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page !== undefined) {
      queryParams.append("page", params.page.toString());
    }
    if (params.limit !== undefined) {
      queryParams.append("limit", params.limit.toString());
    }
    if (params.status_paid) {
      queryParams.append("status_paid", params.status_paid);
    }
    if (params.search) {
      queryParams.append("search", params.search);
    }
    if (params.marked_attended) {
      queryParams.append("marked_attended", params.marked_attended);
    }
    if (params.sort_by) {
      queryParams.append("sort_by", params.sort_by);
    }
    if (params.sort_order) {
      queryParams.append("sort_order", params.sort_order);
    }
    if (params.start_date) {
      queryParams.append("start_date", params.start_date);
    }
    if (params.end_date) {
      queryParams.append("end_date", params.end_date);
    }

    const queryString = queryParams.toString();
    const endpoint = `/registrations/iba/${eventId}${queryString ? `?${queryString}` : ""}`;
    
    const response = await apiClient.get<IBARegistrationsResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updateIBARegistrationAttendance(
  eventId: string,
  registrationId: string,
  data: UpdateAttendanceRequest
): Promise<UpdateAttendanceResponse> {
  try {
    const response = await apiClient.put<UpdateAttendanceResponse>(
      `/registrations/iba/${eventId}/${registrationId}/attendance`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Invitation Card interfaces and functions
export interface SendInvitationResponse {
  success: boolean;
  message?: string;
  data?: {
    registrationId: string;
    email: string;
    messageId: string;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface BulkInvitationResult {
  registrationId: string;
  email: string;
  success: boolean;
  messageId?: string;
  error?: string;
}

export interface BulkInvitationResponse {
  success: boolean;
  message?: string;
  data?: {
    total: number;
    succeeded: number;
    failed: number;
    results?: BulkInvitationResult[];
    errors?: BulkInvitationResult[];
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface BulkInvitationRequest {
  registrationIds: string[];
}

// Send invitation card to IBB registration
export async function sendIBBInvitationCard(
  eventId: string,
  registrationId: string
): Promise<SendInvitationResponse> {
  try {
    const response = await apiClient.post<SendInvitationResponse>(
      `/registrations/ibb/${eventId}/${registrationId}/invitation`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Send invitation card to IBA registration
export async function sendIBAInvitationCard(
  eventId: string,
  registrationId: string
): Promise<SendInvitationResponse> {
  try {
    const response = await apiClient.post<SendInvitationResponse>(
      `/registrations/iba/${eventId}/${registrationId}/invitation`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Send bulk invitation cards to IBB registrations
export async function sendBulkIBBInvitationCards(
  eventId: string,
  data: BulkInvitationRequest
): Promise<BulkInvitationResponse> {
  try {
    const response = await apiClient.post<BulkInvitationResponse>(
      `/registrations/ibb/${eventId}/invitations/bulk`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Send bulk invitation cards to IBA registrations
export async function sendBulkIBAInvitationCards(
  eventId: string,
  data: BulkInvitationRequest
): Promise<BulkInvitationResponse> {
  try {
    const response = await apiClient.post<BulkInvitationResponse>(
      `/registrations/iba/${eventId}/invitations/bulk`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Download invitation card as PDF - IBB
export async function downloadIBBInvitationCardPDF(
  eventId: string,
  registrationId: string
): Promise<Blob> {
  try {
    const response = await apiClient.get(
      `/registrations/ibb/${eventId}/${registrationId}/invitation/download`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Download invitation card as PDF - IBA
export async function downloadIBAInvitationCardPDF(
  eventId: string,
  registrationId: string
): Promise<Blob> {
  try {
    const response = await apiClient.get(
      `/registrations/iba/${eventId}/${registrationId}/invitation/download`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

// QR Code scanning interfaces and functions
export interface ScanQRRequest {
  qrData: string;
}

export interface ScanQRResponse {
  success: boolean;
  message?: string;
  data?: {
    registration: {
      id: string;
      name: string;
      email: string;
      companyName?: string;
      markedAttended: boolean;
      updatedAt: string;
      alreadyAttended?: boolean;
    };
    event: {
      id: string;
      title: string;
      edition?: string;
    };
    scannedBy?: {
      id: string;
      email: string;
    };
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

// Scan QR code (full data)
export async function scanQRCode(data: ScanQRRequest): Promise<ScanQRResponse> {
  try {
    const response = await apiClient.post<ScanQRResponse>(
      `/registrations/scan-qr`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Scan QR code by registration ID
export async function scanQRCodeByRegistrationId(
  registrationId: string,
  eventId: string
): Promise<ScanQRResponse> {
  try {
    const response = await apiClient.post<ScanQRResponse>(
      `/registrations/scan-qr/${registrationId}?eventId=${eventId}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Excel/CSV Import interfaces and functions
export interface ImportResult {
  index: number;
  row: number;
  success: boolean;
  registration?: {
    id: string;
    email: string;
    fullName?: string;
    name?: string;
    createdAt: string;
  };
}

export interface ImportError {
  index: number;
  row: number;
  email?: string;
  error: string;
  code: string;
}

export interface MappingError {
  row: number;
  error: string;
  data?: Record<string, unknown>;
}

export interface ImportResponse {
  success: boolean;
  message?: string;
  data?: {
    total: number;
    succeeded: number;
    failed: number;
    mappingErrors: number;
    results?: ImportResult[];
    errors?: ImportError[];
    mappingErrorsDetails?: MappingError[];
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

// Import IBA registrations from Excel or CSV
export async function importIBARegistrations(
  eventId: string,
  file: File
): Promise<ImportResponse> {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<ImportResponse>(
      `/registrations/iba/${eventId}/import`,
      formData
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Import IBB registrations from Excel or CSV (Event ID in path)
export async function importIBBRegistrations(
  eventId: string,
  file: File
): Promise<ImportResponse> {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<ImportResponse>(
      `/registrations/ibb/${eventId}/import`,
      formData
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Import IBB registrations from Excel or CSV (Event ID in body - for multiple events)
export async function importIBBRegistrationsWithEventIdInBody(
  file: File,
  eventId?: string
): Promise<ImportResponse> {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (eventId) {
      formData.append('eventId', eventId);
    }

    const response = await apiClient.post<ImportResponse>(
      `/registrations/ibb/import`,
      formData
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Notification types and interfaces
export type NotificationType = 
  | "event_created"
  | "event_updated"
  | "event_cancelled"
  | "registration_confirmed"
  | "registration_cancelled"
  | "payment_completed"
  | "payment_failed"
  | "message_received"
  | "system_alert"
  | "system_maintenance";

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  isRead: boolean;
  readAt: string | null;
  relatedId?: string;
  relatedType?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export interface NotificationsQueryParams {
  page?: number;
  limit?: number;
  unread_only?: boolean;
  type?: NotificationType;
}

export interface NotificationsResponse {
  success: boolean;
  data?: {
    notifications: Notification[];
    pagination: EventsPagination;
    unreadCount: number;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface NotificationResponse {
  success: boolean;
  data?: {
    notification: Notification;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface MarkNotificationsReadRequest {
  notificationIds: string[];
}

export interface MarkNotificationsReadResponse {
  success: boolean;
  message?: string;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface CreateNotificationRequest {
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  relatedId?: string;
  relatedType?: string;
  metadata?: Record<string, unknown>;
}

export interface NotificationStatsResponse {
  success: boolean;
  data?: {
    total: number;
    unread: number;
    byType: Array<{
      type: NotificationType;
      count: number;
    }>;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

// Notification API functions
export async function getNotifications(params: NotificationsQueryParams = {}): Promise<NotificationsResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page !== undefined) {
      queryParams.append("page", params.page.toString());
    }
    if (params.limit !== undefined) {
      queryParams.append("limit", params.limit.toString());
    }
    if (params.unread_only !== undefined) {
      queryParams.append("unread_only", params.unread_only.toString());
    }
    if (params.type) {
      queryParams.append("type", params.type);
    }

    const queryString = queryParams.toString();
    const endpoint = `/notifications${queryString ? `?${queryString}` : ""}`;
    
    const response = await apiClient.get<NotificationsResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getNotificationById(notificationId: string): Promise<NotificationResponse> {
  try {
    const response = await apiClient.get<NotificationResponse>(`/notifications/${notificationId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function markNotificationsRead(data: MarkNotificationsReadRequest): Promise<MarkNotificationsReadResponse> {
  try {
    const response = await apiClient.put<MarkNotificationsReadResponse>("/notifications/mark-read", data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function markAllNotificationsRead(): Promise<MarkNotificationsReadResponse> {
  try {
    const response = await apiClient.put<MarkNotificationsReadResponse>("/notifications/mark-all-read", {});
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function deleteNotification(notificationId: string): Promise<MarkNotificationsReadResponse> {
  try {
    const response = await apiClient.delete<MarkNotificationsReadResponse>(`/notifications/${notificationId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Admin notification functions
export async function createNotification(data: CreateNotificationRequest): Promise<NotificationResponse> {
  try {
    const response = await apiClient.post<NotificationResponse>("/notifications", data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getNotificationStats(): Promise<NotificationStatsResponse> {
  try {
    const response = await apiClient.get<NotificationStatsResponse>("/notifications/admin/stats");
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Dashboard API functions
export interface DashboardStatsQueryParams {
  start_date?: string;
  end_date?: string;
}

export async function getDashboardStats(params: DashboardStatsQueryParams = {}): Promise<DashboardStatsResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.start_date) {
      queryParams.append("start_date", params.start_date);
    }
    if (params.end_date) {
      queryParams.append("end_date", params.end_date);
    }

    const queryString = queryParams.toString();
    const endpoint = `/dashboard/stats${queryString ? `?${queryString}` : ""}`;
    
    const response = await apiClient.get<DashboardStatsResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export interface EventsChartQueryParams {
  start_date?: string;
  end_date?: string;
  period?: "week" | "month" | "year";
}

export async function getEventsChartData(params: EventsChartQueryParams = {}): Promise<EventsChartResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.start_date) {
      queryParams.append("start_date", params.start_date);
    }
    if (params.end_date) {
      queryParams.append("end_date", params.end_date);
    }
    if (params.period) {
      queryParams.append("period", params.period);
    }

    const queryString = queryParams.toString();
    const endpoint = `/dashboard/events/chart${queryString ? `?${queryString}` : ""}`;
    
    const response = await apiClient.get<EventsChartResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export interface RegistrationsChartQueryParams {
  start_date?: string;
  end_date?: string;
  event_id?: string;
}

export async function getRegistrationsChartData(params: RegistrationsChartQueryParams = {}): Promise<RegistrationsChartResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.start_date) {
      queryParams.append("start_date", params.start_date);
    }
    if (params.end_date) {
      queryParams.append("end_date", params.end_date);
    }
    if (params.event_id) {
      queryParams.append("event_id", params.event_id);
    }

    const queryString = queryParams.toString();
    const endpoint = `/dashboard/registrations/chart${queryString ? `?${queryString}` : ""}`;
    
    const response = await apiClient.get<RegistrationsChartResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export interface RecentRegistrationsQueryParams {
  limit?: number;
}

export async function getRecentRegistrations(params: RecentRegistrationsQueryParams = {}): Promise<RecentRegistrationsResponse> {
  try {
    const queryParams = new URLSearchParams();

    if (params.limit !== undefined) {
      queryParams.append("limit", params.limit.toString());
    }

    const queryString = queryParams.toString();
    const endpoint = `/dashboard/registrations/recent${queryString ? `?${queryString}` : ""}`;

    const response = await apiClient.get<RecentRegistrationsResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export interface ActivityLogUser {
  fname: string;
  lname: string;
  email: string;
}

export interface ActivityLog {
  id: string;
  method: string;
  path: string;
  action: string;
  actorType: string;
  resourceType: string | null;
  resourceId: string | null;
  statusCode: number | null;
  durationMs: number | null;
  userId: string | null;
  apiTokenId: string | null;
  apiTokenName: string | null;
  metadata: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  user: ActivityLogUser | null;
}

export interface ActivityLogsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  /** @deprecated use `action` — still sent as `event_type` for API compatibility */
  event_type?: string;
  action?: string;
  /** @deprecated use `resource_type` */
  entity_type?: string;
  resource_type?: string;
  method?: string;
  actor_type?: string;
  user_id?: string;
  start_date?: string;
  end_date?: string;
}

export interface ActivityLogsResponse {
  success: boolean;
  data?: {
    logs: ActivityLog[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export async function getActivityLogs(params: ActivityLogsQueryParams = {}): Promise<ActivityLogsResponse> {
  try {
    const queryParams = new URLSearchParams();
    if (params.page !== undefined) queryParams.append("page", params.page.toString());
    if (params.limit !== undefined) queryParams.append("limit", params.limit.toString());
    if (params.search) queryParams.append("search", params.search);
    const action = params.action ?? params.event_type;
    if (action) queryParams.append("action", action);
    const resourceType = params.resource_type ?? params.entity_type;
    if (resourceType) queryParams.append("resource_type", resourceType);
    if (params.method) queryParams.append("method", params.method);
    if (params.actor_type) queryParams.append("actor_type", params.actor_type);
    if (params.user_id) queryParams.append("user_id", params.user_id);
    if (params.start_date) queryParams.append("start_date", params.start_date);
    if (params.end_date) queryParams.append("end_date", params.end_date);

    const queryString = queryParams.toString();
    const endpoint = `/dashboard/activity-logs${queryString ? `?${queryString}` : ""}`;
    const response = await apiClient.get<ActivityLogsResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export interface BlogPostsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  sort_by?: "views" | "createdAt" | "title";
  sort_order?: "asc" | "desc";
}

export async function getBlogPosts(params: BlogPostsQueryParams = {}): Promise<BlogPostsResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page !== undefined) {
      queryParams.append("page", params.page.toString());
    }
    if (params.limit !== undefined) {
      queryParams.append("limit", params.limit.toString());
    }
    if (params.search) {
      queryParams.append("search", params.search);
    }
    if (params.sort_by) {
      queryParams.append("sort_by", params.sort_by);
    }
    if (params.sort_order) {
      queryParams.append("sort_order", params.sort_order);
    }

    const queryString = queryParams.toString();
    const endpoint = `/blog/posts${queryString ? `?${queryString}` : ""}`;
    
    const response = await apiClient.get<BlogPostsResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export interface TopBlogPostsQueryParams {
  limit?: number;
  period?: "day" | "week" | "month" | "year" | "all";
}

export async function getTopBlogPosts(params: TopBlogPostsQueryParams = {}): Promise<TopBlogPostsResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.limit !== undefined) {
      queryParams.append("limit", params.limit.toString());
    }
    if (params.period) {
      queryParams.append("period", params.period);
    }

    const queryString = queryParams.toString();
    const endpoint = `/dashboard/blog/top-posts${queryString ? `?${queryString}` : ""}`;
    
    const response = await apiClient.get<TopBlogPostsResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export interface BlogTrafficQueryParams {
  start_date?: string;
  end_date?: string;
  period?: "week" | "month" | "year";
}

export async function getBlogTraffic(params: BlogTrafficQueryParams = {}): Promise<BlogTrafficResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.start_date) {
      queryParams.append("start_date", params.start_date);
    }
    if (params.end_date) {
      queryParams.append("end_date", params.end_date);
    }
    if (params.period) {
      queryParams.append("period", params.period);
    }

    const queryString = queryParams.toString();
    const endpoint = `/dashboard/blog/traffic${queryString ? `?${queryString}` : ""}`;
    
    const response = await apiClient.get<BlogTrafficResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export interface BlogStatsQueryParams {
  start_date?: string;
  end_date?: string;
}

export async function getBlogStats(params: BlogStatsQueryParams = {}): Promise<BlogStatsResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.start_date) {
      queryParams.append("start_date", params.start_date);
    }
    if (params.end_date) {
      queryParams.append("end_date", params.end_date);
    }

    const queryString = queryParams.toString();
    const endpoint = `/dashboard/blog/stats${queryString ? `?${queryString}` : ""}`;
    
    const response = await apiClient.get<BlogStatsResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Blog CMS API interfaces and functions
export interface BlogAuthor {
  id: string;
  fname: string;
  lname: string;
  avatar: string | null;
  email: string;
  phone: string;
  role: string;
}

export interface BlogMedia {
  id: string;
  title: string;
  type: string;
  isPublic: boolean;
  url: string;
  createdAt: string;
}

export interface BlogRelatedBio {
  id: string;
  name: string;
  profile: string;
  title: string;
  company: string;
  category: string;
}

export interface BlogEvent {
  id: string;
  title: string;
  type: string;
  edition: string;
  category: string;
  description: string;
  eventPlace: string;
  eventDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface Blog {
  id: string;
  title: string;
  imgUrl: string;
  quote: string;
  content: string;
  category: string;
  isPublished: boolean;
  userId: string;
  eventId: string | null;
  eventType?: string;
  userFname?: string;
  userLname?: string;
  userAvatar?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BlogDetails extends Blog {
  author: BlogAuthor;
  media: BlogMedia[];
  relatedBios: BlogRelatedBio[];
}

export interface BlogsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  published?: boolean;
  type?: string;
}

export interface BlogsResponse {
  success: boolean;
  data?: {
    blogs: Blog[];
    pagination: EventsPagination;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
  cached?: boolean;
}

export interface BlogDetailsResponse {
  success: boolean;
  data?: {
    blog: BlogDetails;
    event: BlogEvent | null;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
  cached?: boolean;
}

export interface BlogCategoriesResponse {
  success: boolean;
  data?: {
    categories: string[];
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface CreateBlogRequest {
  title: string;
  imgUrl: string;
  quote: string;
  content: string;
  category: string;
  isPublished?: boolean;
  eventId?: string | null;
  bioIds?: string[];
}

export interface UpdateBlogRequest {
  title?: string;
  imgUrl?: string;
  quote?: string;
  content?: string;
  category?: string;
  isPublished?: boolean;
  eventId?: string | null;
  bioIds?: string[];
}

export interface BlogResponse {
  success: boolean;
  message?: string;
  data?: {
    blog: Blog;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

// Blog CMS API functions
export async function getBlogs(params: BlogsQueryParams = {}): Promise<BlogsResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page !== undefined) {
      queryParams.append("page", params.page.toString());
    }
    if (params.limit !== undefined) {
      queryParams.append("limit", params.limit.toString());
    }
    if (params.search) {
      queryParams.append("search", params.search);
    }
    if (params.category) {
      queryParams.append("category", params.category);
    }
    if (params.published !== undefined) {
      queryParams.append("published", params.published.toString());
    }
    if (params.type) {
      queryParams.append("type", params.type);
    }

    const queryString = queryParams.toString();
    const endpoint = `/blogs${queryString ? `?${queryString}` : ""}`;
    
    const response = await apiClient.get<BlogsResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getBlogById(blogId: string): Promise<BlogDetailsResponse> {
  try {
    const response = await apiClient.get<BlogDetailsResponse>(`/blogs/${blogId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getBlogCategories(): Promise<BlogCategoriesResponse> {
  try {
    const response = await apiClient.get<BlogCategoriesResponse>("/blogs/categories/list");
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function createBlog(data: CreateBlogRequest): Promise<BlogResponse> {
  try {
    const response = await apiClient.post<BlogResponse>("/blogs", data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updateBlog(blogId: string, data: UpdateBlogRequest): Promise<BlogResponse> {
  try {
    const response = await apiClient.put<BlogResponse>(`/blogs/${blogId}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function deleteBlog(blogId: string): Promise<BlogResponse> {
  try {
    const response = await apiClient.delete<BlogResponse>(`/blogs/${blogId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Media API interfaces
export interface Media {
  id: string;
  title: string;
  type: string;
  isPublic: boolean;
  url: string;
  eventId: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
  fileSize?: number;
  contentType?: string;
  event?: {
    id: string;
    title: string;
    category: string;
  };
  user?: {
    id: string;
    fname: string;
    lname: string;
    email: string;
  };
}

export interface MediaPagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface MediaListResponse {
  success: boolean;
  data?: {
    media: Media[];
    pagination: MediaPagination;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
  cached?: boolean;
}

export interface MediaResponse {
  success: boolean;
  message?: string;
  data?: Media;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface MediaQueryParams {
  page?: number;
  limit?: number;
  isPublic?: boolean;
  eventId?: string;
  search?: string;
}

export interface UploadSingleMediaRequest {
  file: File;
  title: string;
  eventId: string;
  isPublic?: boolean;
  type?: string;
}

export interface UploadMultipleMediaRequest {
  files: File[];
  title?: string;
  eventId: string;
  isPublic?: boolean;
  type?: string;
}

export interface UploadMultipleMediaResponse {
  success: boolean;
  message?: string;
  data?: {
    uploaded: Media[];
    failed: Array<{
      filename: string;
      error: string;
    }>;
    total: number;
    successful: number;
    failures: number;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface CreateMediaRequest {
  title: string;
  isPublic?: boolean;
  url?: string;
  eventId: string;
}

export interface UpdateMediaRequest {
  title?: string;
  isPublic?: boolean;
  url?: string;
}

export interface MediaByEventResponse {
  success: boolean;
  data?: Media[];
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface MediaByUserResponse {
  success: boolean;
  data?: Media[];
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface MediaStatsResponse {
  success: boolean;
  data?: {
    total: number;
    public: number;
    private: number;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface DeleteMediaResponse {
  success: boolean;
  message?: string;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

// Media API functions
export async function getMedia(params: MediaQueryParams = {}): Promise<MediaListResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page !== undefined) {
      queryParams.append("page", params.page.toString());
    }
    if (params.limit !== undefined) {
      queryParams.append("limit", params.limit.toString());
    }
    if (params.isPublic !== undefined) {
      queryParams.append("isPublic", params.isPublic.toString());
    }
    if (params.eventId) {
      queryParams.append("eventId", params.eventId);
    }
    if (params.search) {
      queryParams.append("search", params.search);
    }

    const queryString = queryParams.toString();
    const endpoint = `/media${queryString ? `?${queryString}` : ""}`;
    
    const response = await apiClient.get<MediaListResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getMediaById(mediaId: string): Promise<MediaResponse> {
  try {
    const response = await apiClient.get<MediaResponse>(`/media/${mediaId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function uploadSingleMedia(data: UploadSingleMediaRequest): Promise<MediaResponse> {
  try {
    const formData = new FormData();
    formData.append("file", data.file);
    formData.append("title", data.title);
    formData.append("eventId", data.eventId);
    if (data.isPublic !== undefined) {
      formData.append("isPublic", data.isPublic.toString());
    }
    if (data.type) {
      formData.append("type", data.type);
    }

    // Axios interceptor will handle Content-Type automatically for FormData
    const response = await apiClient.post<MediaResponse>("/media/upload", formData);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function uploadMultipleMedia(data: UploadMultipleMediaRequest): Promise<UploadMultipleMediaResponse> {
  try {
    const formData = new FormData();
    
    // Append all files with the same field name "files"
    data.files.forEach((file) => {
      formData.append("files", file);
    });
    
    formData.append("eventId", data.eventId);
    if (data.title) {
      formData.append("title", data.title);
    }
    if (data.isPublic !== undefined) {
      formData.append("isPublic", data.isPublic.toString());
    }
    if (data.type) {
      formData.append("type", data.type);
    }

    // Axios interceptor will handle Content-Type automatically for FormData
    const response = await apiClient.post<UploadMultipleMediaResponse>("/media/upload/multiple", formData);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function createMedia(data: CreateMediaRequest): Promise<MediaResponse> {
  try {
    const response = await apiClient.post<MediaResponse>("/media", data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updateMedia(mediaId: string, data: UpdateMediaRequest): Promise<MediaResponse> {
  try {
    const response = await apiClient.put<MediaResponse>(`/media/${mediaId}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function deleteMedia(mediaId: string): Promise<DeleteMediaResponse> {
  try {
    const response = await apiClient.delete<DeleteMediaResponse>(`/media/${mediaId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getMediaByEventId(eventId: string): Promise<MediaByEventResponse> {
  try {
    const response = await apiClient.get<MediaByEventResponse>(`/media/event/${eventId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getMediaByUserId(userId: string): Promise<MediaByUserResponse> {
  try {
    const response = await apiClient.get<MediaByUserResponse>(`/media/user/${userId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getMediaStats(): Promise<MediaStatsResponse> {
  try {
    const response = await apiClient.get<MediaStatsResponse>("/media/admin/stats");
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Users API interfaces
export interface User {
  id: string;
  fname: string;
  lname: string;
  email: string;
  phone: string;
  role: string;
  isStaff: boolean;
  canLogin: boolean;
  avatar: string | null;
  isEmailVerified: boolean;
  isPhoneVerified: boolean;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UserPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface UserProfileResponse {
  success: boolean;
  data?: {
    user: User;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface UpdateUserProfileRequest {
  fname?: string;
  lname?: string;
  phone?: string;
  avatar?: string | null;
  isStaff?: boolean;
  canLogin?: boolean;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface ChangePasswordResponse {
  success: boolean;
  message?: string;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
  details?: {
    field?: string;
    suggestion?: string;
  };
}

export interface UsersQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  /** Role slug (must match `roles.name` / users.role enum) */
  role?: string;
}

export interface UsersListResponse {
  success: boolean;
  data?: {
    users: User[];
    pagination: UserPagination;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
  cached?: boolean;
}

export interface UserResponse {
  success: boolean;
  message?: string;
  data?: {
    user: User;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
  cached?: boolean;
}

export interface UpdateUserStatusRequest {
  isActive: boolean;
}

export interface UpdateUserStatusResponse {
  success: boolean;
  message?: string;
  data?: {
    user: {
      id: string;
      fname: string;
      lname: string;
      email: string;
      isActive: boolean;
    };
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface DeleteUserResponse {
  success: boolean;
  message?: string;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface CreateUserRequest {
  fname: string;
  lname: string;
  email: string;
  password: string;
  phone?: string;
  /** Role slug from `GET /roles` (e.g. user, admin, super-admin) */
  role?: string;
  isStaff?: boolean;
  canLogin?: boolean;
  avatar?: string | null;
  isActive?: boolean;
}

export interface UpdateUserRequest {
  fname?: string;
  lname?: string;
  email?: string;
  phone?: string;
  /** Role slug from `GET /roles` */
  role?: string;
  isStaff?: boolean;
  canLogin?: boolean;
  avatar?: string | null;
  isActive?: boolean;
}

// Users API functions
export async function getUserProfile(): Promise<UserProfileResponse> {
  try {
    const response = await apiClient.get<UserProfileResponse>("/users/profile");
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updateUserProfile(data: UpdateUserProfileRequest): Promise<UserProfileResponse> {
  try {
    const response = await apiClient.put<UserProfileResponse>("/users/profile", data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function changePassword(data: ChangePasswordRequest): Promise<ChangePasswordResponse> {
  try {
    // Support both POST and PUT methods as per documentation
    const response = await apiClient.post<ChangePasswordResponse>("/users/change-password", data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getUsers(params: UsersQueryParams = {}): Promise<UsersListResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page !== undefined) {
      queryParams.append("page", params.page.toString());
    }
    if (params.limit !== undefined) {
      queryParams.append("limit", params.limit.toString());
    }
    if (params.search) {
      queryParams.append("search", params.search);
    }
    if (params.role) {
      queryParams.append("role", params.role);
    }

    const queryString = queryParams.toString();
    const endpoint = `/users${queryString ? `?${queryString}` : ""}`;
    
    const response = await apiClient.get<UsersListResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getUserById(userId: string): Promise<UserResponse> {
  try {
    const response = await apiClient.get<UserResponse>(`/users/${userId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updateUserStatus(userId: string, data: UpdateUserStatusRequest): Promise<UpdateUserStatusResponse> {
  try {
    const response = await apiClient.put<UpdateUserStatusResponse>(`/users/${userId}/status`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function deleteUser(userId: string): Promise<DeleteUserResponse> {
  try {
    const response = await apiClient.delete<DeleteUserResponse>(`/users/${userId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function createUser(data: CreateUserRequest): Promise<UserResponse> {
  try {
    const response = await apiClient.post<UserResponse>("/users", data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updateUser(userId: string, data: UpdateUserRequest): Promise<UserResponse> {
  try {
    const response = await apiClient.put<UserResponse>(`/users/${userId}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Speaker API interfaces
export interface Speaker {
  id: string;
  name: string;
  title: string | null;
  company: string | null;
  bio: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  linkedin: string | null;
  twitter: string | null;
  avatar: string | null;
  type: "keynote" | "panelist" | "moderator" | "presenter" | "workshop_leader";
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  events?: SpeakerEventAssignment[];
}

export interface SpeakerEventAssignment {
  id: string;
  eventId: string;
  sessionTitle: string | null;
  sessionDescription: string | null;
  sessionTime: string | null;
  sessionDuration: number | null;
  displayOrder: number;
  isVisible: boolean;
  eventTitle?: string;
  eventDate?: string;
  eventPlace?: string;
}

export interface SpeakersQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  type?: string;
  is_active?: string;
}

export interface SpeakersListResponse {
  success: boolean;
  data?: {
    speakers: Speaker[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
  cached?: boolean;
}

export interface SpeakerResponse {
  success: boolean;
  message?: string;
  data?: {
    speaker: Speaker;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
  cached?: boolean;
}

export interface CreateSpeakerRequest {
  name: string;
  title?: string;
  company?: string;
  bio?: string;
  email?: string;
  phone?: string;
  website?: string;
  linkedin?: string;
  twitter?: string;
  avatar?: string | null;
  type?: "keynote" | "panelist" | "moderator" | "presenter" | "workshop_leader";
}

export interface UpdateSpeakerRequest {
  name?: string;
  title?: string;
  company?: string;
  bio?: string;
  email?: string;
  phone?: string;
  website?: string;
  linkedin?: string;
  twitter?: string;
  avatar?: string | null;
  type?: "keynote" | "panelist" | "moderator" | "presenter" | "workshop_leader";
  isActive?: boolean;
}

export interface AssignSpeakerToEventRequest {
  eventId: string;
  sessionTitle?: string;
  sessionDescription?: string;
  sessionTime?: string;
  sessionDuration?: number;
  displayOrder?: number;
  isVisible?: boolean;
}

export interface AssignSpeakerToEventResponse {
  success: boolean;
  message?: string;
  data?: {
    assignment: {
      id: string;
      speakerId: string;
      eventId: string;
      sessionTitle: string | null;
      sessionDescription: string | null;
      sessionTime: string | null;
      sessionDuration: number | null;
      displayOrder: number;
      isVisible: boolean;
      createdAt: string;
    };
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface SpeakerTypesResponse {
  success: boolean;
  data?: {
    types: string[];
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface DeleteSpeakerResponse {
  success: boolean;
  message?: string;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface RemoveSpeakerFromEventResponse {
  success: boolean;
  message?: string;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

// Speaker API functions
export async function getSpeakers(params: SpeakersQueryParams = {}): Promise<SpeakersListResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page !== undefined) {
      queryParams.append("page", params.page.toString());
    }
    if (params.limit !== undefined) {
      queryParams.append("limit", params.limit.toString());
    }
    if (params.search) {
      queryParams.append("search", params.search);
    }
    if (params.type) {
      queryParams.append("type", params.type);
    }
    if (params.is_active !== undefined) {
      queryParams.append("is_active", params.is_active);
    }

    const queryString = queryParams.toString();
    const endpoint = `/speakers${queryString ? `?${queryString}` : ""}`;
    
    const response = await apiClient.get<SpeakersListResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getSpeakerById(speakerId: string): Promise<SpeakerResponse> {
  try {
    const response = await apiClient.get<SpeakerResponse>(`/speakers/${speakerId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function createSpeaker(data: CreateSpeakerRequest): Promise<SpeakerResponse> {
  try {
    const response = await apiClient.post<SpeakerResponse>("/speakers", data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updateSpeaker(speakerId: string, data: UpdateSpeakerRequest): Promise<SpeakerResponse> {
  try {
    const response = await apiClient.put<SpeakerResponse>(`/speakers/${speakerId}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function deleteSpeaker(speakerId: string): Promise<DeleteSpeakerResponse> {
  try {
    const response = await apiClient.delete<DeleteSpeakerResponse>(`/speakers/${speakerId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function assignSpeakerToEvent(speakerId: string, data: AssignSpeakerToEventRequest): Promise<AssignSpeakerToEventResponse> {
  try {
    const response = await apiClient.post<AssignSpeakerToEventResponse>(`/speakers/${speakerId}/assign-event`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function removeSpeakerFromEvent(speakerId: string, eventId: string): Promise<RemoveSpeakerFromEventResponse> {
  try {
    const response = await apiClient.delete<RemoveSpeakerFromEventResponse>(`/speakers/${speakerId}/events/${eventId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getSpeakerTypes(): Promise<SpeakerTypesResponse> {
  try {
    const response = await apiClient.get<SpeakerTypesResponse>("/speakers/types/list");
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Bio API interfaces
export interface RelatedBlog {
  id: string;
  title: string;
  imgUrl: string | null;
  quote: string | null;
  category: string;
  isPublished: boolean;
  createdAt: string;
}

export interface Bio {
  id: string;
  name: string;
  profile: string;
  title: string | null;
  company: string | null;
  category: string;
  author: string;
  story: string | null;
  createdAt: string;
  updatedAt: string;
  relatedBlogs?: RelatedBlog[];
}

export interface BiosQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
}

export interface BiosListResponse {
  success: boolean;
  data?: {
    bios: Bio[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface BioResponse {
  success: boolean;
  message?: string;
  data?: {
    bio: Bio;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface CreateBioRequest {
  name: string;
  profile: string;
  title?: string;
  company?: string;
  category: string;
  author: string;
  story?: string;
}

export interface UpdateBioRequest {
  name?: string;
  profile?: string;
  title?: string;
  company?: string;
  category?: string;
  author?: string;
  story?: string;
}

export interface BioCategoriesResponse {
  success: boolean;
  data?: {
    categories: string[];
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface DeleteBioResponse {
  success: boolean;
  message?: string;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

// Bio API functions
export async function getBios(params: BiosQueryParams = {}): Promise<BiosListResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page !== undefined) {
      queryParams.append("page", params.page.toString());
    }
    if (params.limit !== undefined) {
      queryParams.append("limit", params.limit.toString());
    }
    if (params.search) {
      queryParams.append("search", params.search);
    }
    if (params.category) {
      queryParams.append("category", params.category);
    }

    const queryString = queryParams.toString();
    const endpoint = `/bios${queryString ? `?${queryString}` : ""}`;
    
    const response = await apiClient.get<BiosListResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getBioById(bioId: string): Promise<BioResponse> {
  try {
    const response = await apiClient.get<BioResponse>(`/bios/${bioId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function createBio(data: CreateBioRequest): Promise<BioResponse> {
  try {
    const response = await apiClient.post<BioResponse>("/bios", data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updateBio(bioId: string, data: UpdateBioRequest): Promise<BioResponse> {
  try {
    const response = await apiClient.put<BioResponse>(`/bios/${bioId}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function deleteBio(bioId: string): Promise<DeleteBioResponse> {
  try {
    const response = await apiClient.delete<DeleteBioResponse>(`/bios/${bioId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getBioCategories(): Promise<BioCategoriesResponse> {
  try {
    const response = await apiClient.get<BioCategoriesResponse>("/bios/categories/list");
    return response.data;
  } catch (error) {
    throw error;
  }
}

// ============================================================================
// Permissions and Roles API
// ============================================================================

// Permission Interfaces
export interface Permission {
  id: string;
  name: string;
  description: string;
  category: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PermissionsQueryParams {
  category?: string;
  isActive?: boolean;
  search?: string;
}

export interface PermissionsListResponse {
  success: boolean;
  data?: Permission[];
  count?: number;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface PermissionResponse {
  success: boolean;
  data?: Permission;
  message?: string;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface CreatePermissionRequest {
  name: string;
  description: string;
  category: string;
  isActive?: boolean;
}

export interface UpdatePermissionRequest {
  name?: string;
  description?: string;
  category?: string;
  isActive?: boolean;
}

// User Permission Interfaces
export interface UserPermission {
  id: string;
  permissionId: string;
  permissionName: string;
  permissionDescription: string;
  permissionCategory: string;
  grantedBy: string;
  grantedAt: string;
  revokedAt: string | null;
  revokedBy: string | null;
  isActive: boolean;
  createdAt: string;
}

export interface UserPermissionsResponse {
  success: boolean;
  data?: UserPermission[];
  count?: number;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface AssignPermissionToUserRequest {
  userId: string;
  permissionId: string;
}

export interface BulkAssignPermissionsToUserRequest {
  userId: string;
  permissionIds: string[];
}

export interface BulkAssignPermissionsResponse {
  success: boolean;
  message?: string;
  data?: {
    new: number;
    reactivated: number;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

// Role Permission Interfaces (Legacy Enum-based)
export interface RolePermission {
  id: string;
  role: "user" | "admin" | "super-admin";
  permissionId: string;
  permissionName: string;
  permissionDescription: string;
  permissionCategory: string;
  isActive: boolean;
  createdAt: string;
}

export interface RolePermissionsResponse {
  success: boolean;
  data?: RolePermission[];
  count?: number;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface AssignPermissionToRoleRequest {
  role: "user" | "admin" | "super-admin";
  permissionId: string;
}

export interface BulkAssignPermissionsToRoleRequest {
  role: "user" | "admin" | "super-admin";
  permissionIds: string[];
}

// Role Interfaces
export interface Role {
  id: string;
  name: string;
  displayName: string;
  description: string;
  level: number;
  isSystem: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  permissions?: Permission[];
  userCount?: number;
}

export interface RolesQueryParams {
  isActive?: boolean;
  isSystem?: boolean;
  search?: string;
}

export interface RolesListResponse {
  success: boolean;
  data?: Role[];
  count?: number;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface RoleResponse {
  success: boolean;
  data?: Role;
  message?: string;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface CreateRoleRequest {
  name: string;
  displayName: string;
  description: string;
  level: number;
  isSystem?: boolean;
  isActive?: boolean;
  /** Permission UUIDs to assign to the role when it is created */
  permissionIds?: string[];
}

export interface UpdateRoleRequest {
  displayName?: string;
  description?: string;
  level?: number;
  isActive?: boolean;
}

// Role Assignment Interfaces
export interface UserWithRole {
  id: string;
  fname: string;
  lname: string;
  email: string;
  phone: string;
  role: string;
  isActive: boolean;
  createdAt: string;
}

export interface UsersWithRoleResponse {
  success: boolean;
  data?: UserWithRole[];
  count?: number;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface AssignRoleToUserRequest {
  userId: string;
  roleId: string;
}

export interface AssignRoleToUserResponse {
  success: boolean;
  message?: string;
  data?: {
    userId: string;
    roleId: string;
    roleName: string;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

// Role Permissions (via Roles API) Interfaces
export interface AssignPermissionToRoleByIdRequest {
  roleId: string;
  permissionId: string;
}

export interface BulkAssignPermissionsToRoleByIdRequest {
  roleId: string;
  permissionIds: string[];
}

// Permissions API Functions
export async function getPermissions(params: PermissionsQueryParams = {}): Promise<PermissionsListResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.category) {
      queryParams.append("category", params.category);
    }
    if (params.isActive !== undefined) {
      queryParams.append("isActive", params.isActive.toString());
    }
    if (params.search) {
      queryParams.append("search", params.search);
    }

    const queryString = queryParams.toString();
    const endpoint = `/permissions${queryString ? `?${queryString}` : ""}`;
    
    const response = await apiClient.get<PermissionsListResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getPermissionById(permissionId: string): Promise<PermissionResponse> {
  try {
    const response = await apiClient.get<PermissionResponse>(`/permissions/${permissionId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function createPermission(data: CreatePermissionRequest): Promise<PermissionResponse> {
  try {
    const response = await apiClient.post<PermissionResponse>("/permissions", data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updatePermission(permissionId: string, data: UpdatePermissionRequest): Promise<PermissionResponse> {
  try {
    const response = await apiClient.put<PermissionResponse>(`/permissions/${permissionId}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function deletePermission(permissionId: string): Promise<PermissionResponse> {
  try {
    const response = await apiClient.delete<PermissionResponse>(`/permissions/${permissionId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// User Permissions API Functions
export async function getUserPermissions(userId: string): Promise<UserPermissionsResponse> {
  try {
    const response = await apiClient.get<UserPermissionsResponse>(`/permissions/users/${userId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function assignPermissionToUser(data: AssignPermissionToUserRequest): Promise<PermissionResponse> {
  try {
    const response = await apiClient.post<PermissionResponse>("/permissions/users/assign", data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function bulkAssignPermissionsToUser(data: BulkAssignPermissionsToUserRequest): Promise<BulkAssignPermissionsResponse> {
  try {
    const response = await apiClient.post<BulkAssignPermissionsResponse>("/permissions/users/bulk-assign", data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function revokePermissionFromUser(userId: string, permissionId: string): Promise<PermissionResponse> {
  try {
    const response = await apiClient.delete<PermissionResponse>(`/permissions/users/${userId}/permissions/${permissionId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Role Permissions API Functions (Legacy Enum-based)
export async function getRolePermissions(role: "user" | "admin" | "super-admin"): Promise<RolePermissionsResponse> {
  try {
    const response = await apiClient.get<RolePermissionsResponse>(`/permissions/roles/${role}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function assignPermissionToRole(data: AssignPermissionToRoleRequest): Promise<PermissionResponse> {
  try {
    const response = await apiClient.post<PermissionResponse>("/permissions/roles/assign", data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function bulkAssignPermissionsToRole(data: BulkAssignPermissionsToRoleRequest): Promise<BulkAssignPermissionsResponse> {
  try {
    const response = await apiClient.post<BulkAssignPermissionsResponse>("/permissions/roles/bulk-assign", data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function revokePermissionFromRole(role: "user" | "admin" | "super-admin", permissionId: string): Promise<PermissionResponse> {
  try {
    const response = await apiClient.delete<PermissionResponse>(`/permissions/roles/${role}/permissions/${permissionId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Roles API Functions
export async function getRoles(params: RolesQueryParams = {}): Promise<RolesListResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.isActive !== undefined) {
      queryParams.append("isActive", params.isActive.toString());
    }
    if (params.isSystem !== undefined) {
      queryParams.append("isSystem", params.isSystem.toString());
    }
    if (params.search) {
      queryParams.append("search", params.search);
    }

    const queryString = queryParams.toString();
    const endpoint = `/roles${queryString ? `?${queryString}` : ""}`;
    
    const response = await apiClient.get<RolesListResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getRoleById(roleId: string): Promise<RoleResponse> {
  try {
    const response = await apiClient.get<RoleResponse>(`/roles/${roleId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function createRole(data: CreateRoleRequest): Promise<RoleResponse> {
  try {
    const response = await apiClient.post<RoleResponse>("/roles", data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updateRole(roleId: string, data: UpdateRoleRequest): Promise<RoleResponse> {
  try {
    const response = await apiClient.put<RoleResponse>(`/roles/${roleId}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function deleteRole(roleId: string): Promise<RoleResponse> {
  try {
    const response = await apiClient.delete<RoleResponse>(`/roles/${roleId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Role Assignments API Functions
export async function getUsersWithRole(roleId: string): Promise<UsersWithRoleResponse> {
  try {
    const response = await apiClient.get<UsersWithRoleResponse>(`/roles/${roleId}/users`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function assignRoleToUser(data: AssignRoleToUserRequest): Promise<AssignRoleToUserResponse> {
  try {
    const response = await apiClient.post<AssignRoleToUserResponse>("/roles/assign", data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function removeRoleFromUser(userId: string): Promise<RoleResponse> {
  try {
    const response = await apiClient.delete<RoleResponse>(`/roles/users/${userId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Role Permissions (via Roles API) Functions
export async function getRolePermissionsById(roleId: string): Promise<RolePermissionsResponse> {
  try {
    const response = await apiClient.get<RolePermissionsResponse>(`/roles/${roleId}/permissions`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function assignPermissionToRoleById(data: AssignPermissionToRoleByIdRequest): Promise<PermissionResponse> {
  try {
    const response = await apiClient.post<PermissionResponse>("/roles/permissions/assign", data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function bulkAssignPermissionsToRoleById(data: BulkAssignPermissionsToRoleByIdRequest): Promise<BulkAssignPermissionsResponse> {
  try {
    const response = await apiClient.post<BulkAssignPermissionsResponse>("/roles/permissions/bulk-assign", data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function revokePermissionFromRoleById(roleId: string, permissionId: string): Promise<PermissionResponse> {
  try {
    const response = await apiClient.delete<PermissionResponse>(`/roles/${roleId}/permissions/${permissionId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// ============================================================================
// Feedback API
// ============================================================================

// Feedback Interfaces
export interface Feedback {
  id: string;
  name: string;
  email: string;
  type: "general" | "event" | "service";
  message: string;
  eventId: string | null;
  createdAt: string;
  updatedAt?: string;
  event?: {
    id: string;
    title: string;
    type: string;
  };
}

export interface FeedbacksQueryParams {
  page?: number;
  limit?: number;
  type?: "general" | "event" | "service";
  eventId?: string;
  search?: string;
}

export interface FeedbacksPagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface FeedbacksListResponse {
  success: boolean;
  data?: {
    feedbacks: Feedback[];
    pagination: FeedbacksPagination;
  };
  message?: string;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface FeedbackResponse {
  success: boolean;
  data?: Feedback;
  message?: string;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface CreateFeedbackRequest {
  name: string;
  email: string;
  type?: "general" | "event" | "service";
  message: string;
  eventId?: string;
}

export interface UpdateFeedbackRequest {
  name?: string;
  email?: string;
  type?: "general" | "event" | "service";
  message?: string;
  eventId?: string;
}

export interface FeedbackStats {
  total: number;
  recent: number;
  byType: {
    general: number;
    event: number;
    service: number;
  };
}

export interface FeedbackStatsResponse {
  success: boolean;
  data?: FeedbackStats;
  message?: string;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

// Feedback API Functions
export async function submitFeedback(data: CreateFeedbackRequest): Promise<FeedbackResponse> {
  try {
    const response = await apiClient.post<FeedbackResponse>("/feedbacks", data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getFeedbacks(params: FeedbacksQueryParams = {}): Promise<FeedbacksListResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page !== undefined) {
      queryParams.append("page", params.page.toString());
    }
    if (params.limit !== undefined) {
      queryParams.append("limit", params.limit.toString());
    }
    if (params.type) {
      queryParams.append("type", params.type);
    }
    if (params.eventId) {
      queryParams.append("eventId", params.eventId);
    }
    if (params.search) {
      queryParams.append("search", params.search);
    }

    const queryString = queryParams.toString();
    const endpoint = `/feedbacks${queryString ? `?${queryString}` : ""}`;
    
    const response = await apiClient.get<FeedbacksListResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getFeedbackById(feedbackId: string): Promise<FeedbackResponse> {
  try {
    const response = await apiClient.get<FeedbackResponse>(`/feedbacks/${feedbackId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updateFeedback(feedbackId: string, data: UpdateFeedbackRequest): Promise<FeedbackResponse> {
  try {
    const response = await apiClient.put<FeedbackResponse>(`/feedbacks/${feedbackId}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function deleteFeedback(feedbackId: string): Promise<FeedbackResponse> {
  try {
    const response = await apiClient.delete<FeedbackResponse>(`/feedbacks/${feedbackId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getFeedbackStats(): Promise<FeedbackStatsResponse> {
  try {
    const response = await apiClient.get<FeedbackStatsResponse>("/feedbacks/stats");
    return response.data;
  } catch (error) {
    throw error;
  }
}

// ============================================================================
// Testimonials API
// ============================================================================

// Testimonial Interfaces
export interface Testimonial {
  id: string;
  name: string;
  title: string;
  organization: string;
  message: string;
  rating: number;
  image: string | null;
  about: string;
  isPublished: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface TestimonialsQueryParams {
  page?: number;
  limit?: number;
  rating?: number;
}

export interface TestimonialsAdminQueryParams {
  page?: number;
  limit?: number;
  isPublished?: boolean;
  rating?: number;
}

export interface TestimonialsPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface TestimonialsListResponse {
  success: boolean;
  data?: {
    testimonials: Testimonial[];
    pagination: TestimonialsPagination;
  };
  message?: string;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface TestimonialResponse {
  success: boolean;
  data?: Testimonial;
  message?: string;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface CreateTestimonialRequest {
  name: string;
  title: string;
  organization: string;
  message: string;
  rating: number;
  image?: string;
  about: string;
  isPublished?: boolean;
}

export interface UpdateTestimonialRequest {
  name?: string;
  title?: string;
  organization?: string;
  message?: string;
  rating?: number;
  image?: string;
  about?: string;
  isPublished?: boolean;
}

export interface TestimonialStats {
  total: number;
  published: number;
  unpublished: number;
  averageRating: number;
  ratingDistribution: {
    "5": number;
    "4": number;
    "3": number;
    "2": number;
    "1": number;
  };
}

export interface TestimonialStatsResponse {
  success: boolean;
  data?: TestimonialStats;
  message?: string;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

// Testimonials API Functions
// Public endpoints
export async function getTestimonials(params: TestimonialsQueryParams = {}): Promise<TestimonialsListResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page !== undefined) {
      queryParams.append("page", params.page.toString());
    }
    if (params.limit !== undefined) {
      queryParams.append("limit", params.limit.toString());
    }
    if (params.rating !== undefined) {
      queryParams.append("rating", params.rating.toString());
    }

    const queryString = queryParams.toString();
    const endpoint = `/testimonials${queryString ? `?${queryString}` : ""}`;
    
    const response = await apiClient.get<TestimonialsListResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getTestimonialById(testimonialId: string): Promise<TestimonialResponse> {
  try {
    const response = await apiClient.get<TestimonialResponse>(`/testimonials/${testimonialId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Admin endpoints
export async function getAllTestimonialsAdmin(params: TestimonialsAdminQueryParams = {}): Promise<TestimonialsListResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page !== undefined) {
      queryParams.append("page", params.page.toString());
    }
    if (params.limit !== undefined) {
      queryParams.append("limit", params.limit.toString());
    }
    if (params.isPublished !== undefined) {
      queryParams.append("isPublished", params.isPublished.toString());
    }
    if (params.rating !== undefined) {
      queryParams.append("rating", params.rating.toString());
    }

    const queryString = queryParams.toString();
    const endpoint = `/testimonials/admin/all${queryString ? `?${queryString}` : ""}`;
    
    const response = await apiClient.get<TestimonialsListResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function createTestimonial(data: CreateTestimonialRequest): Promise<TestimonialResponse> {
  try {
    const response = await apiClient.post<TestimonialResponse>("/testimonials", data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updateTestimonial(testimonialId: string, data: UpdateTestimonialRequest): Promise<TestimonialResponse> {
  try {
    const response = await apiClient.put<TestimonialResponse>(`/testimonials/${testimonialId}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function toggleTestimonialPublish(testimonialId: string): Promise<TestimonialResponse> {
  try {
    const response = await apiClient.patch<TestimonialResponse>(`/testimonials/${testimonialId}/publish`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function deleteTestimonial(testimonialId: string): Promise<TestimonialResponse> {
  try {
    const response = await apiClient.delete<TestimonialResponse>(`/testimonials/${testimonialId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getTestimonialStats(): Promise<TestimonialStatsResponse> {
  try {
    const response = await apiClient.get<TestimonialStatsResponse>("/testimonials/admin/stats");
    return response.data;
  } catch (error) {
    throw error;
  }
}

// ============================================================================
// Sponsors & Partners API
// ============================================================================

// Sponsor Interfaces
export interface Sponsor {
  id: string;
  name: string;
  description: string | null;
  logo: string | null;
  website: string | null;
  email: string | null;
  phone: string | null;
  contactPerson: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

export type SponsorTier = "platinum" | "gold" | "silver" | "bronze" | "partner" | "media_partner";

export interface EventSponsor {
  id: string;
  eventId: string;
  sponsorId: string;
  tier: SponsorTier;
  amount: number;
  benefits: string | null;
  displayOrder: number;
  isVisible: boolean;
  createdAt: string;
  updatedAt: string;
  sponsor?: Sponsor;
  event?: {
    id: string;
    title: string;
    type: string;
  };
}

export interface SponsorsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
  includeDeleted?: boolean;
}

export interface EventSponsorsQueryParams {
  tier?: SponsorTier;
  isVisible?: boolean;
}

export interface SponsorsPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface SponsorsListResponse {
  success: boolean;
  data?: {
    sponsors: Sponsor[];
    pagination: SponsorsPagination;
  };
  message?: string;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface SponsorResponse {
  success: boolean;
  data?: Sponsor;
  message?: string;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface CreateSponsorRequest {
  name: string;
  description?: string;
  logo?: string;
  website?: string;
  email?: string;
  phone?: string;
  contactPerson?: string;
  isActive?: boolean;
}

export interface UpdateSponsorRequest {
  name?: string;
  description?: string;
  logo?: string;
  website?: string;
  email?: string;
  phone?: string;
  contactPerson?: string;
  isActive?: boolean;
}

export interface AddSponsorToEventRequest {
  // Option 1: Use existing sponsor (provide sponsorId)
  sponsorId?: string;
  
  // Option 2: Create new sponsor (provide sponsor details)
  // If sponsorId is not provided, these fields are required
  name?: string;
  description?: string;
  logo?: string;
  website?: string;
  email?: string;
  phone?: string;
  contactPerson?: string;
  isActive?: boolean;
  
  // Event-specific sponsorship details (required)
  tier: SponsorTier;
  amount?: number;
  benefits?: string;
  displayOrder?: number;
  isVisible?: boolean;
}

export interface UpdateEventSponsorRequest {
  tier?: SponsorTier;
  amount?: number;
  benefits?: string;
  displayOrder?: number;
  isVisible?: boolean;
}

export interface EventSponsorsListResponse {
  success: boolean;
  data?: {
    sponsors: EventSponsor[];
  };
  message?: string;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface SponsorEventsListResponse {
  success: boolean;
  data?: {
    events: Array<{
      id: string;
      title: string;
      type: string;
      eventDate: string;
      eventSponsor: EventSponsor;
    }>;
  };
  message?: string;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

// Sponsors API Functions
export async function getSponsors(params: SponsorsQueryParams = {}): Promise<SponsorsListResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page !== undefined) {
      queryParams.append("page", params.page.toString());
    }
    if (params.limit !== undefined) {
      queryParams.append("limit", params.limit.toString());
    }
    if (params.search) {
      queryParams.append("search", params.search);
    }
    if (params.isActive !== undefined) {
      queryParams.append("isActive", params.isActive.toString());
    }
    if (params.includeDeleted !== undefined) {
      queryParams.append("includeDeleted", params.includeDeleted.toString());
    }

    const queryString = queryParams.toString();
    const endpoint = `/sponsors${queryString ? `?${queryString}` : ""}`;
    
    const response = await apiClient.get<SponsorsListResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getSponsorById(sponsorId: string): Promise<SponsorResponse> {
  try {
    const response = await apiClient.get<SponsorResponse>(`/sponsors/${sponsorId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function createSponsor(data: CreateSponsorRequest): Promise<SponsorResponse> {
  try {
    const response = await apiClient.post<SponsorResponse>("/sponsors", data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updateSponsor(sponsorId: string, data: UpdateSponsorRequest): Promise<SponsorResponse> {
  try {
    const response = await apiClient.put<SponsorResponse>(`/sponsors/${sponsorId}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function deleteSponsor(sponsorId: string): Promise<SponsorResponse> {
  try {
    const response = await apiClient.delete<SponsorResponse>(`/sponsors/${sponsorId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Event-Sponsor Relationship Functions
/**
 * Add sponsor to event
 * 
 * This function supports two modes:
 * 1. Create new sponsor: Provide sponsor details (name, description, logo, etc.) without sponsorId
 *    - Can use FormData for logo upload (multipart/form-data)
 *    - Can use JSON with logo URL
 * 2. Use existing sponsor: Provide sponsorId to add existing sponsor to event
 * 
 * @param eventId - The event ID to add the sponsor to
 * @param data - Sponsor details and event-specific sponsorship info
 *              - If creating new sponsor: include name, description, logo, etc.
 *              - If using existing: include sponsorId
 *              - Always include tier and optional event-specific fields
 * @returns Event sponsor relationship data
 */
export async function addSponsorToEvent(
  eventId: string, 
  data: AddSponsorToEventRequest | FormData
): Promise<EventSponsorsListResponse> {
  try {
    // Supports both JSON (AddSponsorToEventRequest) and FormData (for logo uploads)
    // Axios interceptor will automatically handle Content-Type for FormData
    const response = await apiClient.post<EventSponsorsListResponse>(
      `/sponsors/events/${eventId}/sponsors`, 
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getEventSponsors(eventId: string, params: EventSponsorsQueryParams = {}): Promise<EventSponsorsListResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.tier) {
      queryParams.append("tier", params.tier);
    }
    if (params.isVisible !== undefined) {
      queryParams.append("isVisible", params.isVisible.toString());
    }

    const queryString = queryParams.toString();
    const endpoint = `/sponsors/events/${eventId}/sponsors${queryString ? `?${queryString}` : ""}`;
    
    const response = await apiClient.get<EventSponsorsListResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updateEventSponsor(eventId: string, sponsorId: string, data: UpdateEventSponsorRequest): Promise<EventSponsorsListResponse> {
  try {
    const response = await apiClient.put<EventSponsorsListResponse>(`/sponsors/events/${eventId}/sponsors/${sponsorId}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function removeSponsorFromEvent(eventId: string, sponsorId: string): Promise<SponsorResponse> {
  try {
    const response = await apiClient.delete<SponsorResponse>(`/sponsors/events/${eventId}/sponsors/${sponsorId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getSponsorEvents(sponsorId: string): Promise<SponsorEventsListResponse> {
  try {
    const response = await apiClient.get<SponsorEventsListResponse>(`/sponsors/${sponsorId}/events`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// ============================================================================
// Newsletter API
// ============================================================================

// Newsletter Interfaces
export type SubscriberStatus = "subscribed" | "unsubscribed" | "bounced" | "complained";

export interface Subscriber {
  id: string;
  email: string;
  status: SubscriberStatus;
  subscribedAt: string;
  unsubscribedAt?: string | null;
}

export interface NewsletterSubscribersQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: SubscriberStatus;
}

export interface NewsletterSubscribersPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface StatusCount {
  status: SubscriberStatus;
  count: number;
}

export interface NewsletterSubscribersListResponse {
  success: boolean;
  data?: {
    subscribers: Subscriber[];
    pagination: NewsletterSubscribersPagination;
    statusCounts?: StatusCount[];
  };
  message?: string;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface SubscriberResponse {
  success: boolean;
  data?: Subscriber;
  message?: string;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface SubscribeRequest {
  email: string;
}

export interface UnsubscribeRequest {
  email: string;
}

export interface SubscriptionStatusResponse {
  success: boolean;
  data?: {
    subscribed: boolean;
    status: SubscriberStatus;
    subscribedAt?: string;
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface UpdateSubscriberStatusRequest {
  status: SubscriberStatus;
}

export interface SendNewsletterRequest {
  subject: string;
  content: string;
  template?: string;
  sendToAll?: boolean;
  subscriberIds?: string[];
  scheduledAt?: string;
}

export interface NewsletterResult {
  id: string;
  subject: string;
  recipientCount: number;
  sentCount: number;
  failedCount: number;
  template?: string;
  scheduledAt?: string | null;
  sentAt?: string;
  sentBy?: string;
}

export interface EmailResult {
  email: string;
  success: boolean;
  error?: string;
}

export interface SendNewsletterResponse {
  success: boolean;
  message?: string;
  data?: {
    newsletter: NewsletterResult;
    recipients: Array<{
      id: string;
      email: string;
    }>;
    emailResults: EmailResult[];
  };
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

export interface NewsletterStats {
  total: number;
  active: number;
  recent: number;
  byStatus: StatusCount[];
}

export interface NewsletterStatsResponse {
  success: boolean;
  data?: NewsletterStats;
  message?: string;
  error?: string;
  code?: string;
  statusCode?: number;
  timestamp?: string;
}

// Newsletter API Functions
// Public endpoints
export async function subscribeToNewsletter(data: SubscribeRequest): Promise<SubscriberResponse> {
  try {
    const response = await apiClient.post<SubscriberResponse>("/newsletter/subscribe", data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function unsubscribeFromNewsletter(data: UnsubscribeRequest): Promise<SubscriberResponse> {
  try {
    const response = await apiClient.post<SubscriberResponse>("/newsletter/unsubscribe", data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function checkSubscriptionStatus(email: string): Promise<SubscriptionStatusResponse> {
  try {
    const response = await apiClient.get<SubscriptionStatusResponse>(`/newsletter/status/${encodeURIComponent(email)}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Admin endpoints
export async function getNewsletterSubscribers(params: NewsletterSubscribersQueryParams = {}): Promise<NewsletterSubscribersListResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page !== undefined) {
      queryParams.append("page", params.page.toString());
    }
    if (params.limit !== undefined) {
      queryParams.append("limit", params.limit.toString());
    }
    if (params.search) {
      queryParams.append("search", params.search);
    }
    if (params.status) {
      queryParams.append("status", params.status);
    }

    const queryString = queryParams.toString();
    const endpoint = `/newsletter/subscribers${queryString ? `?${queryString}` : ""}`;
    
    const response = await apiClient.get<NewsletterSubscribersListResponse>(endpoint);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getNewsletterSubscriberById(subscriberId: string): Promise<SubscriberResponse> {
  try {
    const response = await apiClient.get<SubscriberResponse>(`/newsletter/subscribers/${subscriberId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updateSubscriberStatus(subscriberId: string, data: UpdateSubscriberStatusRequest): Promise<SubscriberResponse> {
  try {
    const response = await apiClient.put<SubscriberResponse>(`/newsletter/subscribers/${subscriberId}/status`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function deleteNewsletterSubscriber(subscriberId: string): Promise<SubscriberResponse> {
  try {
    const response = await apiClient.delete<SubscriberResponse>(`/newsletter/subscribers/${subscriberId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function sendNewsletter(data: SendNewsletterRequest): Promise<SendNewsletterResponse> {
  try {
    const response = await apiClient.post<SendNewsletterResponse>("/newsletter/send", data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getNewsletterStats(): Promise<NewsletterStatsResponse> {
  try {
    const response = await apiClient.get<NewsletterStatsResponse>("/newsletter/stats");
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Export axios instance for direct use if needed
export { apiClient };
